import { useMutation, useQueryClient } from "@tanstack/react-query";
import { configuration } from "../../../configuration";
import { CommentSubject } from "../../../constants";
import { FetchError } from "../../types";
import { useAccessToken } from "../../use-access-token";
import { queryKeys } from "../queryKeys";

type Variables = {
  subjectType: CommentSubject;
  subjectGuid: string;
  commentGuid: string;
};

const useDeleteCommentRequest = () => {
  const { getAccessToken } = useAccessToken();

  async function deleteCommentRequest(variables: Variables) {
    const accessToken = await getAccessToken();

    const response = await fetch(`${configuration.apiRootUrl}/comments/${variables.commentGuid}`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      method: "DELETE",
    });

    if (!response.ok) {
      throw new FetchError(response);
    }
  }

  return { deleteCommentRequest };
};

const useDeleteComment = () => {
  const { deleteCommentRequest } = useDeleteCommentRequest();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteCommentRequest,
    onMutate: async (_variables) => {},
    onError: (_error, _variables, _context) => {},
    onSuccess: (_data, variables) => {
      switch (variables.subjectType) {
        case CommentSubject.ActionItem:
          queryClient.refetchQueries(queryKeys.actionItems.item(variables.subjectGuid));
          break;
        case CommentSubject.Issue:
          queryClient.refetchQueries(queryKeys.issues.getIssueFeed(variables.subjectGuid));
          break;
        case CommentSubject.TaskItem:
          queryClient.invalidateQueries(queryKeys.taskItems.getTaskItemFeed(variables.subjectGuid));
          break;
        case CommentSubject.Team:
          queryClient.invalidateQueries(queryKeys.teams.getTeamFeed(variables.subjectGuid));
          break;
      }
    },
    onSettled: (_data, _error, _variables) => {},
  });
};

export { useDeleteComment };
